/**
 * useUrlSearchInput — input de búsqueda cuyo valor vive en la URL (`?q=`).
 *
 * Mientras el usuario teclea, el input muestra su edición pendiente (el
 * tecleo se siente instantáneo); tras `delayMs` sin teclear la confirma
 * con `commit` (navegación con `replace`, sin una entrada de historial
 * por carácter). Fuera de esa edición el input muestra el valor de la
 * URL, así un cambio externo (back/forward, un link) se ve sin copiar la
 * URL a un estado.
 */

import { useEffect, useState } from 'react'

import { useDebouncedValue } from './useDebouncedValue'

export function useUrlSearchInput(
  urlValue: string,
  commit: (value: string) => void,
  delayMs: number = 300,
): [string, (value: string) => void] {
  // Edición pendiente; `null` cuando el input refleja la URL.
  const [draft, setDraft] = useState<string | null>(null)
  const debouncedDraft = useDebouncedValue(draft, delayMs)
  const settled = draft !== null && debouncedDraft === draft

  // La URL ya tiene la edición y el usuario dejó de teclear: el input
  // vuelve a leer de la URL.
  if (settled && draft === urlValue) {
    setDraft(null)
  }

  useEffect(() => {
    if (settled && draft !== urlValue) commit(draft)
  }, [settled, draft, urlValue, commit])

  return [draft ?? urlValue, setDraft]
}
